/**
 * The public booking API. No session and no cookies that matter: the only
 * credential is an identity token (from a token link or a verified one-time
 * code), sent as `x-velora-booking-identity` so existing-patient visit types
 * and prefill unlock (OB-07, OB-09).
 *
 * A small client over the booking contracts rather than the generated SDK:
 * the SDK module builds every endpoint's schemas at load, which would triple
 * this public page's script. Responses are still validated against the same
 * `@velora/contracts/booking` schemas the API serves.
 */
import {
  AvailabilitySchema,
  HoldSchema,
  IdentifyChallengeSchema,
  IdentityTokenSchema,
  PublicSiteSchema,
  SubmitResultSchema,
  type Attribution,
  type Availability,
  type CallbackRequest,
  type Hold,
  type IdentifyChallenge,
  type Progress,
  type PublicSite,
  type Slot,
  type SubmitRequest,
  type SubmitResult,
} from '@velora/contracts/booking';
import { z } from 'zod';

/** A non-2xx answer; `body` is the API error envelope when it is JSON. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly path: string,
  ) {
    super(`API ${status} on ${path}`);
    this.name = 'ApiError';
  }

  get details(): unknown {
    const b = this.body as { error?: { details?: unknown } } | undefined;
    return b?.error?.details;
  }
}

const MessageSchema = z.object({ message: z.string() });
const ReleasedSchema = z.object({ released: z.boolean() });

export const IDENTITY_HEADER = 'x-velora-booking-identity';

export interface BookingApi {
  site(): Promise<PublicSite>;
  availability(q: {
    typeId: string;
    from?: string;
    days?: number;
    providerId?: string;
    near?: { start: string; end: string; providerId?: string | null };
  }): Promise<Availability>;
  hold(body: {
    typeId: string;
    providerId: string | null;
    start: string;
    visitorId: string;
    replace?: { holdId: string; holdToken: string };
  }): Promise<Hold>;
  release(holdId: string, holdToken: string): Promise<void>;
  submit(body: SubmitRequest): Promise<SubmitResult>;
  progress(body: Progress): Promise<void>;
  callback(
    body: CallbackRequest & { kind: 'callback' | 'emergency' | 'must_call' },
  ): Promise<string>;
  identify(body: {
    firstName: string;
    lastName: string;
    birthdate: string;
  }): Promise<IdentifyChallenge>;
  verify(challengeId: string, code: string): Promise<string>;
}

export function createBookingApi(
  slug: string,
  identityToken: () => string | null,
  baseUrl = '/v1',
  fetchImpl?: typeof fetch,
): BookingApi {
  const doFetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = `${baseUrl.replace(/\/+$/, '')}/booking/public`;
  const site = `/sites/${encodeURIComponent(slug)}`;

  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const token = identityToken();
    if (token) headers[IDENTITY_HEADER] = token;
    const init: RequestInit = { method, headers, credentials: 'omit' };
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const res = await doFetch(`${base}${path}`, init);
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text;
    }
    if (!res.ok) throw new ApiError(res.status, parsed, path);
    return schema.parse(parsed);
  }

  return {
    site: () => call('GET', site, PublicSiteSchema),
    availability: (q) => {
      const qs = new URLSearchParams({ typeId: q.typeId, days: String(q.days ?? 7) });
      if (q.from) qs.set('from', q.from);
      if (q.providerId) qs.set('providerId', q.providerId);
      if (q.near) {
        qs.set('nearStart', q.near.start);
        qs.set('nearEnd', q.near.end);
        if (q.near.providerId) qs.set('nearProviderId', q.near.providerId);
      }
      return call('GET', `${site}/availability?${qs.toString()}`, AvailabilitySchema);
    },
    hold: (body) => call('POST', `${site}/holds`, HoldSchema, body),
    release: async (holdId, holdToken) => {
      await call('POST', `/holds/${encodeURIComponent(holdId)}/release`, ReleasedSchema, {
        holdToken,
      });
    },
    submit: (body) => call('POST', `${site}/requests`, SubmitResultSchema, body),
    progress: async (body) => {
      await call('POST', `${site}/progress`, z.unknown(), body);
    },
    callback: async (body) => (await call('POST', `${site}/callback`, MessageSchema, body)).message,
    identify: (body) => call('POST', `${site}/identify`, IdentifyChallengeSchema, body),
    verify: async (challengeId, code) =>
      (await call('POST', `${site}/verify`, IdentityTokenSchema, { challengeId, code }))
        .identityToken,
  };
}

/** What the visitor should read for a failed call. */
export function messageOf(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
    if (err.status === 429) return 'Too many tries. Wait a minute and try again.';
    if (err.status >= 500) return 'We could not reach the office just now. Try again in a moment.';
    return 'Something went wrong. Try again.';
  }
  if (err instanceof TypeError)
    return 'You seem to be offline. Check your connection and try again.';
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

/** The `details.reason` of an API error, if any. */
export function reasonOf(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.details as { reason?: unknown } | undefined;
  return typeof d?.reason === 'string' ? d.reason : null;
}

/** Alternatives offered with a 409 "slot taken" (OB-04). */
export function alternativesOf(err: unknown): Slot[] {
  if (!(err instanceof ApiError) || err.status !== 409) return [];
  const d = err.details as { alternatives?: unknown } | undefined;
  return Array.isArray(d?.alternatives) ? (d.alternatives as Slot[]) : [];
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export type { Attribution, Hold, Slot };
